import type { AuditState, CandidatePlace } from '@/types/gmb';
import NoResultsCta from '@/pages/gmb-audit/components/NoResultsCta';

interface CandidateSectionProps {
  state: AuditState;
  candidates: CandidatePlace[];
  selected?: CandidatePlace | null;
  onSelect: (candidate: CandidatePlace) => void;
}

function EmptyBlock({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-400 bg-background-100 px-5 py-10 text-center md:px-6 md:py-14">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-foreground-400 ring-1 ring-background-300 md:h-16 md:w-16">
        <i className={`${icon} text-2xl md:text-3xl`} />
      </span>
      <p className="mt-4 text-xl font-semibold text-foreground-800">{title}</p>
      <p className="mt-1.5 max-w-md text-base leading-relaxed text-foreground-600">{description}</p>
    </div>
  );
}

/** 服務區域商家：沒有公開實體地址屬正常狀況。 */
function ServiceAreaTag() {
  return (
    <span className="ds-badge bg-accent-100 text-accent-800 ring-1 ring-accent-200">
      <i className="ri-service-line" />
      服務區域商家
    </span>
  );
}

/**
 * 位置資訊列：有正式地址顯示地址；無地址但 Autocomplete 有地區文字則顯示地區辨識文字
 * （低調樣式，不稱為正式地址）；都沒有則不顯示。
 */
function LocationLine({ candidate }: { candidate: CandidatePlace }) {
  const text = candidate.address || candidate.secondaryText;
  if (!text) return null;
  const icon = candidate.address ? 'ri-map-pin-line' : 'ri-map-pin-2-line';
  return (
    <p className="mt-1.5 flex items-center gap-2 text-base text-foreground-600">
      <i className={icon} />
      <span className="truncate">{text}</span>
    </p>
  );
}

export default function CandidateSection({ state, candidates, selected, onSelect }: CandidateSectionProps) {
  const showList =
    state === 'candidate-results' ||
    state === 'snapshot-loading' ||
    state === 'snapshot-ready' ||
    state === 'snapshot-error';

  return (
    <section className="ds-container py-10 md:py-14">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <h2 className="text-xl text-foreground-950 md:text-2xl">候選店家</h2>
        <span className="rounded-full bg-secondary-100 px-3 py-1 text-sm font-medium text-secondary-800">
          最多顯示 5 筆
        </span>
      </div>

      {state === 'searching' && (
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-base font-semibold text-foreground-700">
            <i className="ri-loader-4-line animate-spin text-lg text-primary-700" />
            正在尋找 Google 商家…
          </p>
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 w-full animate-pulse rounded-2xl border border-background-300 bg-background-100" />
          ))}
        </div>
      )}

      {showList && (
        <div className="grid grid-cols-1 gap-3">
          {candidates.slice(0, 5).map((candidate) => {
            const isSelected = selected?.placeId === candidate.placeId;
            return (
              <button
                key={candidate.placeId}
                type="button"
                onClick={() => onSelect(candidate)}
                className={`flex min-h-[72px] w-full cursor-pointer flex-col items-start gap-3 rounded-2xl border bg-background-50 p-4 text-left transition-all hover:border-primary-400 hover:bg-primary-50/50 hover:shadow-card md:flex-row md:items-center md:justify-between md:p-5 ${
                  isSelected ? 'border-primary-400 ring-1 ring-primary-200' : 'border-background-300'
                }`}
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-base font-bold text-foreground-950 md:text-lg">{candidate.name}</p>
                  <LocationLine candidate={candidate} />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {candidate.primaryCategory && (
                      <span className="ds-badge bg-secondary-100 text-secondary-800">
                        <i className="ri-price-tag-3-line" />
                        {candidate.primaryCategory}
                      </span>
                    )}
                    {candidate.pureServiceAreaBusiness && <ServiceAreaTag />}
                  </div>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg bg-primary-50 px-4 py-2.5 text-base font-semibold text-primary-800">
                  <i className="ri-checkbox-circle-line text-lg" />
                  這是我要健診的店家
                  <i className="ri-arrow-right-s-line text-lg" />
                </span>
              </button>
            );
          })}
        </div>
      )}

      {state === 'auditing' && (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-background-300 bg-background-50 px-6 py-14 text-center">
          <i className="ri-loader-4-line animate-spin text-4xl text-primary-700" />
          <p className="mt-4 text-base font-semibold text-foreground-800">正在進行健診分析…</p>
          <p className="mt-1.5 text-base text-foreground-600">正在比對公開資料與附近同業。</p>
        </div>
      )}

      {state === 'no-results' && (
        <>
          <EmptyBlock
            icon="ri-search-eye-line"
            title="找不到符合的 Google 商家"
            description="目前沒有回傳任何候選店家。請嘗試：1. 輸入 Google Maps 上的完整店名　2. 加入城市或行政區　3. 使用完整地址再次搜尋。"
          />
          <NoResultsCta />
        </>
      )}

      {(state === 'initial' || state === 'completed') && (
        <EmptyBlock
          icon="ri-store-line"
          title="尚未查詢店家"
          description="輸入店家名稱與地區後，符合的候選店家會顯示在這裡，供你選擇正確的健診對象。"
        />
      )}

      {state === 'insufficient-peers' && (
        <EmptyBlock
          icon="ri-group-line"
          title="附近同業樣本不足"
          description="此地區可比較的同業樣本不足，部分同業比較指標將標示為資料不足，不會以 0 分計算。"
        />
      )}
    </section>
  );
}